import React from "react";
import useTranslation from "next-translate/useTranslation";
import Page from "../../components/layout/page";
import NumberDisplay from "../../components/ui/number-display";
import PlayedGames from "./PlayedGames";
import Medal from "../../components/common/medal";
import ProfileMenu from "./ProfileMenu";
import Membership from "./membership/Membership";
import Medals from "./Medals";
import { FaUser } from "react-icons/fa";
import { FaSignal } from "react-icons/fa";
import { FaStar } from "react-icons/fa";
import { FaTrophy } from "react-icons/fa";
import { FaChessKing } from "react-icons/fa";
import { FaChess } from "react-icons/fa";
import Link from "next/link";
import {
  ModeIds,
  MODE_ID_PRACTICE_CHESS,
  MODE_ID_PRACTICE_CHESS960,
  MEMBERSHIP_PAGE,
  LEAGUE_NAME_PLACEMENT,
  MEDALS_PAGE,
} from "@chesshotel/constants";
import { getModeNameCapitalFirst } from "../../util/util";
import styles from "./Profile.module.css";
import { useRouter } from "next/router";
import Head from "next/head";
import { useStats } from "../../redux/stats";
import { useLeagues } from "../../redux/leagues";
import { useUser } from "../../redux/user";

export default function Profile() {
  const { t } = useTranslation("common");
  const { t: profileT } = useTranslation("auth");

  const router = useRouter();
  const { modeId } = router.query;
  const activeModeId = Number(modeId) || MEMBERSHIP_PAGE;

  const { chess, chess960 } = useStats();
  const { leagueScores, seasonNr, lastSeasonNr, lastSeason } = useLeagues();

  const { isGuest } = useUser();

  function renderHeader() {
    const isPractice =
      activeModeId === MODE_ID_PRACTICE_CHESS ||
      activeModeId === MODE_ID_PRACTICE_CHESS960;
    const isLeague =
      activeModeId === ModeIds.BULLET ||
      activeModeId === ModeIds.BLITZ ||
      activeModeId === ModeIds.RAPID ||
      activeModeId === ModeIds.CHESS960 ||
      activeModeId === ModeIds.DAILY;
    // const isMembership = activeModeId === MEMBERSHIP_PAGE;
    const isPlacement =
      isLeague &&
      leagueScores[activeModeId].leagueName === LEAGUE_NAME_PLACEMENT;

    let title;
    if (isLeague && !isPlacement) {
      const leagueName = leagueScores[activeModeId].leagueName;
      title =
        getModeNameCapitalFirst(activeModeId, t).toUpperCase() +
        " " +
        t(leagueName + " league").toUpperCase();
      const rank =
        leagueScores[activeModeId] && leagueScores[activeModeId].rank
          ? leagueScores[activeModeId].rank
          : 0;

      return (
        <div className={styles.header}>
          <div className={styles.headerTitle}>{title}</div>

          <div className={styles.medal}>
            <Link
              className="hover:brightness-[1.07]"
              href={"/leagues/" + activeModeId}
            >
              <Medal
                title={"RANK"}
                rank={rank}
                leagueName={leagueName}
                size="medium"
              />
            </Link>
          </div>
        </div>
      );
    } else {
      if (isPractice) {
        title =
          activeModeId === MODE_ID_PRACTICE_CHESS
            ? profileT("Chess").toUpperCase() +
              " " +
              profileT("Practice games").toUpperCase()
            : "CHESS960 " + profileT("Practice games").toUpperCase();
      } else if (activeModeId === MEMBERSHIP_PAGE) {
        title = profileT("Membership");
      } else if (activeModeId === MEDALS_PAGE) {
        title = t("Medals");
      } else {
        title =
          getModeNameCapitalFirst(activeModeId, t).toUpperCase() +
          " " +
          t("League games");
      }

      return (
        <div className={styles.practiceHeader}>
          <div className={styles.practiceTitle}>{title}</div>
        </div>
      );
    }
  }

  function renderBody() {
    const isLeague =
      activeModeId === ModeIds.BULLET ||
      activeModeId === ModeIds.BLITZ ||
      activeModeId === ModeIds.RAPID ||
      activeModeId === ModeIds.CHESS960 ||
      activeModeId === ModeIds.DAILY;
    const isPractice =
      activeModeId === MODE_ID_PRACTICE_CHESS ||
      activeModeId === MODE_ID_PRACTICE_CHESS960;

    const isPlacement = false; // isLeague && leagueScores[activeModeId].leagueName === LEAGUE_NAME_PLACEMENT

    if (activeModeId === MEMBERSHIP_PAGE) {
      return <Membership />;
    } else if (activeModeId === MEDALS_PAGE) {
      return <Medals />;
    } else if (isPlacement) {
      return <div className={styles.empty}>{t("Not entered in league")}</div>;
    } else {
      let wins, losses, draws, elo, points;
      if (isPractice) {
        const stats =
          activeModeId === MODE_ID_PRACTICE_CHESS ? chess : chess960;
        wins = stats.winsBlack + stats.winsWhite;
        losses = stats.lossesBlack + stats.lossesWhite;
        draws = stats.drawsBlack + stats.drawsWhite;
        elo = stats.elo;
      } else {
        const scores = leagueScores[activeModeId];

        wins = scores.wins || 0;
        losses = scores.losses || 0;
        draws = scores.draws || 0;
        elo = scores.elo || 1200;
        points = scores.points || 0;
      }
      wins = isNaN(wins) ? "" : wins;
      losses = isNaN(losses) ? "" : losses;
      draws = isNaN(draws) ? "" : draws;

      return (
        <div className={styles.statsBody}>
          <div className={styles.seasonStats}>
            {!isPractice && (
              <div className={styles.seasonTitle}>
                {t("Season").toUpperCase()} {seasonNr}
              </div>
            )}

            <div className={styles.stats}>
              <NumberDisplay
                title={t("Rating")}
                number={elo}
                fontawesomeIcon={<FaSignal />}
              />
              {isLeague && (
                <NumberDisplay
                  title={t("Points")}
                  number={points}
                  fontawesomeIcon={<FaStar />}
                />
              )}
            </div>
            <div className={styles.stats}>
              <NumberDisplay
                title={t("Wins")}
                number={wins}
                fontawesomeIcon={<FaTrophy />}
              />
              <NumberDisplay
                title={t("Losses")}
                number={losses}
                fontawesomeIcon={
                  <FaChessKing style={{ transform: "rotate(95deg)" }} />
                }
              />
              <NumberDisplay
                title={profileT("Draws")}
                number={draws}
                fontawesomeIcon={<FaChess />}
              />
            </div>
          </div>
          {lastSeason[activeModeId] && (
            <div className={styles.seasonStats}>
              <div className={styles.seasonTitle}>
                {t("FINAL RESULT")} {t("Season").toUpperCase()} {lastSeasonNr}
              </div>

              <div className={styles.stats}>
                <NumberDisplay
                  title={t("Rating")}
                  number={lastSeason[activeModeId].elo}
                  fontawesomeIcon={<FaSignal />}
                />
                {isLeague && (
                  <NumberDisplay
                    title={t("Points")}
                    number={lastSeason[activeModeId].points}
                    fontawesomeIcon={<FaStar />}
                  />
                )}
              </div>
              <div className={styles.stats}>
                <NumberDisplay
                  title={t("Wins")}
                  number={lastSeason[activeModeId].wins}
                  fontawesomeIcon={<FaTrophy />}
                />
                <NumberDisplay
                  title={t("Losses")}
                  number={lastSeason[activeModeId].losses}
                  fontawesomeIcon={
                    <FaChessKing style={{ transform: "rotate(95deg)" }} />
                  }
                />
                <NumberDisplay
                  title={profileT("Draws")}
                  number={lastSeason[activeModeId].draws}
                  fontawesomeIcon={<FaChess />}
                />
              </div>
            </div>
          )}

          <PlayedGames />
        </div>
      );
    }
  }

  if (isGuest) {
    return null;
  }

  const pageTitle = (
    <div className="flex items-center gap-2 text-2xl font-semibold leading-tight md:text-3xl">
      <span className="text-[0.85em] text-orange-400">
        <FaUser />
      </span>
      <span>{t("Profile")}</span>
    </div>
  );

  return (
    <Page title={pageTitle}>
      <Head>
        <title>{"Chess Hotel | " + t("Profile")}</title>
        <meta name="description" content={t("description")} />
        <meta name="keywords" content={t("keywords")} />
      </Head>
      <div className={styles.wrapper}>
        <ProfileMenu />
        <div className={styles.body}>
          {renderHeader()}
          {renderBody()}
        </div>
      </div>
    </Page>
  );
}
